import { capitalizeFirstName, capitalizeLastName, createCapitalizeHandler } from '@/shared/utils'
import type { PersonalInfoProps } from '@/features/enrollment'

export default function PersonalInfo({ enrollmentData, updateEnrollmentData, errors }: PersonalInfoProps) {
  return (
    <>
      {/* Auto-populated fields */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">List ID</label>
        <input type="text" className="input-field bg-accent-100 cursor-not-allowed" value={enrollmentData.listId || ''} disabled />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          Agent UID <span className="text-red-500">*</span>
        </label>
        <input type="text" className="input-field bg-accent-100 cursor-not-allowed" value={enrollmentData.agentId || ''} disabled />
      </div>

      {/* Full Name of person signing retainer agreement */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          Full Name of person signing retainer agreement
        </label>
        <input 
          type="text" 
          className="input-field bg-accent-100 cursor-not-allowed" 
          value={enrollmentData.retainerAgreementFullName || ''} 
          disabled 
        />
      </div>

      {/* Video Gamer's Name */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          VIDEO GAMER'S NAME <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <input 
              type="text" 
              name="injuredPartyFirstName"
              className={`input-field ${errors.injuredPartyFirstName ? 'border-red-500' : ''}`} 
              placeholder="First Name" 
              value={enrollmentData.callerFirstName || enrollmentData.injuredPartyFirstName || ''} 
              onChange={createCapitalizeHandler(
                (value) => updateEnrollmentData('injuredPartyFirstName', value),
                capitalizeFirstName
              )} 
            />
            <label className="block text-xs text-accent-500 mt-1">First Name</label>
            {errors.injuredPartyFirstName && (
              <p className="mt-1 text-sm text-red-600">{errors.injuredPartyFirstName}</p>
            )}
          </div>
          <div>
            <input 
              type="text" 
              className="input-field" 
              placeholder="Middle Name" 
              value={enrollmentData.injuredPartyMiddleName || ''} 
              onChange={createCapitalizeHandler(
                (value) => updateEnrollmentData('injuredPartyMiddleName', value),
                capitalizeFirstName
              )} 
            />
            <label className="block text-xs text-accent-500 mt-1">Middle Name (optional)</label>
          </div>
          <div>
            <input 
              type="text" 
              name="injuredPartyLastName"
              className={`input-field ${errors.injuredPartyLastName ? 'border-red-500' : ''}`} 
              placeholder="Last Name" 
              value={enrollmentData.callerLastName || enrollmentData.injuredPartyLastName || ''} 
              onChange={createCapitalizeHandler(
                (value) => updateEnrollmentData('injuredPartyLastName', value),
                capitalizeLastName
              )} 
            />
            <label className="block text-xs text-accent-500 mt-1">Last Name</label>
            {errors.injuredPartyLastName && (
              <p className="mt-1 text-sm text-red-600">{errors.injuredPartyLastName}</p>
            )}
          </div>
          <div>
            <input 
              type="text" 
              className="input-field" 
              placeholder="Suffix" 
              value={enrollmentData.injuredPartySuffix || ''} 
              onChange={(e) => updateEnrollmentData('injuredPartySuffix', e.target.value)} 
            />
            <label className="block text-xs text-accent-500 mt-1">Suffix (optional)</label>
          </div>
        </div>
      </div>

      {/* Claimant Deceased - Hide if "Myself" is selected */}
      {enrollmentData.relationToVictim?.toLowerCase() !== 'myself' && (
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Is the Claimant Deceased? <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input type="radio" name="isClaimantDeceased" value="true" checked={enrollmentData.isClaimantDeceased === true} onChange={() => updateEnrollmentData('isClaimantDeceased', true)} className="form-radio text-primary-600" />
              <span className="ml-2 text-accent-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name="isClaimantDeceased" value="false" checked={enrollmentData.isClaimantDeceased === false} onChange={() => updateEnrollmentData('isClaimantDeceased', false)} className="form-radio text-primary-600" />
              <span className="ml-2 text-accent-700">No</span>
            </label>
          </div>
          {errors.isClaimantDeceased && (
            <p className="mt-1 text-sm text-red-600">{errors.isClaimantDeceased}</p>
          )}
        </div>
      )}

      {/* Date of Death (conditional) */}
      {enrollmentData.isClaimantDeceased === true && (
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Date of Death <span className="text-red-500">*</span>
          </label>
          <input 
            type="date" 
            className={`input-field ${errors.dateOfDeath ? 'border-red-500' : ''}`} 
            value={enrollmentData.dateOfDeath} 
            onChange={(e) => updateEnrollmentData('dateOfDeath', e.target.value)} 
          />
          {errors.dateOfDeath && (
            <p className="mt-1 text-sm text-red-600">{errors.dateOfDeath}</p>
          )}
        </div>
      )}

      {/* Gender */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          Gender <span className="text-red-500">*</span>
        </label>
        <select 
          className={`input-field ${errors.gender ? 'border-red-500' : ''}`} 
          value={enrollmentData.gender} 
          onChange={(e) => updateEnrollmentData('gender', e.target.value)}
        >
          <option value="">Select Gender</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
        </select>
        {errors.gender && (
          <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
        )}
      </div>

      {/* Email Address - Read-only if set in Step 1 */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          {enrollmentData.relationToVictim?.toLowerCase() === 'myself' 
            ? 'Injured party email address' 
            : 'Legal guardian\'s email address'
          } <span className="text-red-500">*</span>
        </label>
        <input 
          type="email" 
          name="injuredPartyEmail"
          className={`input-field ${enrollmentData.intakeEmail ? 'bg-gray-100 cursor-not-allowed' : ''} ${errors.injuredPartyEmail ? 'border-red-500' : ''}`} 
          placeholder="Enter email address" 
          value={enrollmentData.injuredPartyEmail || enrollmentData.intakeEmail || ''} 
          onChange={(e) => {
            if (!enrollmentData.intakeEmail) {
              updateEnrollmentData('injuredPartyEmail', e.target.value)
            }
          }}
          readOnly={!!enrollmentData.intakeEmail}
          disabled={!!enrollmentData.intakeEmail}
        />
        {enrollmentData.intakeEmail ? (
          <p className="text-xs text-gray-500 mt-1">Auto-populated from Step 1 and cannot be changed.</p>
        ) : (
          <p className="text-xs text-accent-500 mt-1">Use victim's email if 18+, parent's if minor.</p>
        )}
        {errors.injuredPartyEmail && (
          <p className="mt-1 text-sm text-red-600">{errors.injuredPartyEmail}</p>
        )}
      </div>
    </>
  )
}
